import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Alert, Container, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { storage } from '../../firebaseConfig';
import { ref, deleteObject } from 'firebase/storage';
import { FireStoreProduct } from '../../model/ProducModel';
import { ProductGateway } from '../../gateways/ProductGateway';
import { CategoryGateway } from '../../gateways/CategoryGateway';
import { FireStoreCategory } from '../../model/CategoryModel';
import { Utils } from '../../common/Utils';
import { useGlobal } from '../../contexts/GlobalContext';

const ProductList: React.FC = () => {
    const [products, setProducts] = useState<FireStoreProduct[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<FireStoreProduct | null>(null);
    const { settings } = useGlobal();

    const navigate = useNavigate();

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                var productList = await ProductGateway.GetProducts(null, null);
                productList.sort((a, b) => a.product.name.localeCompare(b.product.name));

                const categoryData = await CategoryGateway.GetCategories();
                const categoryMap: Record<string, FireStoreCategory> = {};
                categoryData.forEach((doc) => {
                    categoryMap[doc.id] = doc;
                });

                const enrichedProductPromise = await Promise.all(
                    productList.map(async (item) => {
                        return {id: item.id, product: {...item.product, category: categoryMap[item.product.category].category.name}};
                    })
                );

                setProducts(enrichedProductPromise);

            } catch (err) {
                setError('Error fetching products.');
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();

    }, []);

    const handleEdit = (id: string) => {
        navigate(Utils.GetUrl(`/admin/product/${id}`));
    };

    const handleDelete = async () => {
        if (!selectedProduct) return;

        try {
            await ProductGateway.DeleteProduct(selectedProduct.id);

            if (selectedProduct.product.imageUrl) {
                const imageRef = ref(storage, selectedProduct.product.imageUrl);
                await deleteObject(imageRef);
            }

            setProducts(products.filter(product => product.id !== selectedProduct.id));
            setShowModal(false);
            alert('Product deleted successfully!');
        } catch (err) {
            console.error(err);
            setError('Error deleting product.');
        }
    };

    const confirmDelete = (product: FireStoreProduct) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    return (
        <Container className="main-container p-3">
            <Row className="align-items-center mb-3">
                <Col>
                    <h2>Product List</h2>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={() => navigate(Utils.GetUrl('/admin/product'))}>Add Product</Button>
                </Col>
            </Row>

            {loading && <Spinner animation="border" />}
            {error && <Alert variant="danger">{error}</Alert>}
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th className="d-none d-md-table-cell">Category</th>
                        <th>Price</th>
                        <th className="d-none d-md-table-cell">Status</th>
                        <th>Image</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((fireStoreProduct) => (
                        <tr key={fireStoreProduct.id}>
                            <td>{fireStoreProduct.product.name}</td>
                            <td className="d-none d-md-table-cell">{fireStoreProduct.product.category}</td>
                            <td>{Utils.FormatCurrency(fireStoreProduct.product.price, settings.currency)}</td>
                            <td className="d-none d-md-table-cell">{fireStoreProduct.product.status}</td>
                            <td>
                                {fireStoreProduct.product.imageUrl && <img src={fireStoreProduct.product.imageUrl} alt={fireStoreProduct.product.name} width="50" />}
                            </td>
                            <td>
                                <Button onClick={() => handleEdit(fireStoreProduct.id)}>Edit</Button>{' '}
                                <Button variant="danger" onClick={() => confirmDelete(fireStoreProduct)}>Delete</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {/* Delete Confirmation Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete <strong>{selectedProduct?.product.name}</strong>? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete}>Delete</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ProductList;